/*
 * Per-tab tenant session state (VIS-326). Two records live in sessionStorage,
 * and neither ever holds a token — only the Auth0 `sub`/`sid` claims and a
 * tenant id. Nothing here authorises anything: the BFF re-checks `?tenant=`
 * on every request.
 *
 * - Held tenant: the tenant opened in this tab, bound to the Auth0 session
 *   (`sub` + `sid`) so a reload keeps it but a new sign-in does not.
 * - Idle record: written only when an idle session ends, so the next sign-in
 *   can restore that tenant once. It is the only state carried across a new
 *   `sid`.
 */

export const HELD_TENANT_KEY = 'visitone.currentTenant'
export const IDLE_RESTORE_KEY = 'visitone.idleRestore'
const LEGACY_TENANT_KEY = 'visitone.activeTenant'

export type HeldTenant = { sub: string; sid: string; tenantId: string }
export type IdleRestore = { sub: string; tenantId: string }

function read<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

function remove(key: string) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // Storage unavailable: nothing is held, so there is nothing to clear.
  }
}

function dropLegacyKey() {
  remove(LEGACY_TENANT_KEY)
}

/** The held tenant id, only when it belongs to this user and this Auth0 session. */
export function readHeldTenant(sub: string, sid: string): string | null {
  dropLegacyKey()
  const held = read<HeldTenant>(HELD_TENANT_KEY)
  if (!held || held.sub !== sub || held.sid !== sid || typeof held.tenantId !== 'string') return null
  return held.tenantId
}

/** Stores the held tenant. Returns false when session storage cannot be written. */
export function writeHeldTenant(held: HeldTenant): boolean {
  try {
    sessionStorage.setItem(HELD_TENANT_KEY, JSON.stringify(held))
    return true
  } catch {
    return false
  }
}

export function clearHeldTenant() {
  remove(HELD_TENANT_KEY)
}

/*
 * When the ID token carries no `sid`, held tenants are stored with an empty
 * `sid` and cannot tell one sign-in from the next. A completed sign-in then
 * drops such a record so it is never reused. The idle record is left alone.
 */
export function clearUnboundHeldTenant() {
  const held = read<HeldTenant>(HELD_TENANT_KEY)
  if (held && !held.sid) clearHeldTenant()
}

export function recordIdleEnd(record: IdleRestore) {
  try {
    sessionStorage.setItem(IDLE_RESTORE_KEY, JSON.stringify(record))
  } catch {
    // Storage unavailable: the next sign-in follows the ordinary rules.
  }
}

/**
 * The tenant to restore after an idle sign-in, for this user only. Reading
 * does not consume the record; `openTenant` does once the held tenant is
 * stored. A record left by another user is removed and never applied.
 */
export function readIdleRestore(sub: string): string | null {
  const record = read<IdleRestore>(IDLE_RESTORE_KEY)
  if (!record) return null
  if (record.sub !== sub || typeof record.tenantId !== 'string') {
    discardIdleRestore()
    return null
  }
  return record.tenantId
}

export function discardIdleRestore() {
  remove(IDLE_RESTORE_KEY)
}

/**
 * The one commit used by every way of opening a tenant. The held tenant is
 * written first; the idle record is removed only when that write succeeded,
 * so a failed write leaves the restore available to the next load. The tenant
 * opens either way.
 */
export function openTenant(held: HeldTenant): boolean {
  const stored = writeHeldTenant(held)
  if (stored) discardIdleRestore()
  return stored
}

/** Explicit sign-out: nothing is carried to the next sign-in. */
export function endSession() {
  clearHeldTenant()
  discardIdleRestore()
  dropLegacyKey()
}
